import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import EmployerLocationMap from '../components/EmployerLocationMap';
import EmployerRatingSection from '../components/EmployerRatingSection';
import toast from 'react-hot-toast';
import {
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  PhoneIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  PlayIcon,
  HeartIcon,
  ArrowLeftIcon,
  TagIcon,
  CurrencyYenIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { API_BASE } from '../lib/config';

const API = API_BASE;

const EmployerProfileDetail = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || '');
  const [applicationMessage, setApplicationMessage] = useState('');
  const [preferredWorkStartTime, setPreferredWorkStartTime] = useState('');
  const [preferredWorkEndTime, setPreferredWorkEndTime] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [eligibilityIssues, setEligibilityIssues] = useState([]);
  const [isEligible, setIsEligible] = useState(true);

  useEffect(() => {
    // 認証状態の読み込み中は待機
    if (authLoading) {
      return;
    }

    // アクセス制御：求職者ログイン後のみアクセス可能
    // Guest Access Allowed
    /*
    if (!user) {
      navigate('/register');
      return;
    }
    
    if (user.user_type !== 'job_seeker') {
      toast.error('事業者の詳細を見るには求職者としてログインしてください');
      navigate('/');
      return;
    }
    */

    fetchProfile();
    checkIdentityVerification();
    checkApplicationEligibility();
  }, [profileId, user, authLoading, navigate]);

  const checkApplicationEligibility = async () => {
    try {
      const response = await fetch(`${API}/users/application-eligibility`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsEligible(data.eligible);
        setEligibilityIssues(data.issues || []);
      }
    } catch (error) {
      console.error('Error checking application eligibility:', error);
    }
  };

  const checkIdentityVerification = async () => {
    try {
      const response = await fetch(`${API}/identity-verification/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIdentityVerified(data.status === 'completed');
      }
    } catch (error) {
      console.error('Error checking identity verification:', error);
      setIdentityVerified(false);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/employer-profiles/${profileId}`);

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        toast.error('事業者プロフィールが見つかりません');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('データの取得に失敗しました');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // マッチング申請
  const handleApply = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // 応募資格チェック
    try {
      const eligibilityResponse = await fetch(`${API}/users/application-eligibility`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (eligibilityResponse.ok) {
        const eligibilityData = await eligibilityResponse.json();

        if (!eligibilityData.eligible) {
          // 不備がある場合は詳細メッセージを表示
          const issues = eligibilityData.issues.join(' ');
          toast.error(issues);
          return;
        }
      } else {
        toast.error('応募資格の確認に失敗しました');
        return;
      }
    } catch (error) {
      console.error('Error checking eligibility:', error);
      toast.error('応募資格の確認に失敗しました');
      return;
    }

    // 希望勤務時間のバリデーション
    if (!preferredWorkStartTime || !preferredWorkEndTime) {
      toast.error('希望勤務時間を入力してください');
      return;
    }

    // 時間の形式チェック（HH:MM）
    const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!timePattern.test(preferredWorkStartTime) || !timePattern.test(preferredWorkEndTime)) {
      toast.error('時間は HH:MM の形式で入力してください');
      return;
    }

    // 開始時間と終了時間の順序チェック
    const startTime = new Date(`2000-01-01T${preferredWorkStartTime}:00`);
    const endTime = new Date(`2000-01-01T${preferredWorkEndTime}:00`);
    if (startTime >= endTime) {
      toast.error('終了時間は開始時間より後にしてください');
      return;
    }

    try {
      setApplying(true);
      const requestData = {
        employer_profile_id: profileId,
        applicant_message: applicationMessage || null,
        preferred_work_start_time: preferredWorkStartTime,
        preferred_work_end_time: preferredWorkEndTime
      };

      if (selectedDate) {
        requestData.work_date = selectedDate;
      }

      const response = await fetch(`${API}/matching-applications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        toast.success('マッチング申請を送信しました！');
        navigate('/mypage?tab=applications');
      } else {
        const error = await response.json();
        toast.error(error.detail || '申請の送信に失敗しました');
      }
    } catch (error) {
      console.error('Error applying:', error);
      toast.error('ネットワークエラーが発生しました');
    } finally {
      setApplying(false);
    }
  };

  // 画像・動画スライダー切り替え
  const nextImage = () => {
    if (profile) {
      const totalImages = profile.images ? profile.images.length : 0;
      const totalVideos = profile.videos ? profile.videos.length : 0;
      const totalMedia = totalImages + totalVideos;

      if (totalMedia > 0) {
        setCurrentImageIndex((prev) =>
          prev === totalMedia - 1 ? 0 : prev + 1
        );
      }
    }
  };

  const prevImage = () => {
    if (profile) {
      const totalImages = profile.images ? profile.images.length : 0;
      const totalVideos = profile.videos ? profile.videos.length : 0;
      const totalMedia = totalImages + totalVideos;

      if (totalMedia > 0) {
        setCurrentImageIndex((prev) =>
          prev === 0 ? totalMedia - 1 : prev - 1
        );
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">プロフィールが見つかりません</h2>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">{profile.business_name}</h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 sm:py-6 max-w-4xl">
        {/* 画像・動画スライダーセクション */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4 sm:mb-6">
          <div className="relative h-48 sm:h-64 md:h-80">
            {/* メディアコンテンツの組み合わせ */}
            {(profile.images && profile.images.length > 0) || (profile.videos && profile.videos.length > 0) ? (
              <>
                {/* 現在のスライドの表示 */}
                {(() => {
                  const totalImages = profile.images ? profile.images.length : 0;
                  const totalVideos = profile.videos ? profile.videos.length : 0;
                  const totalMedia = totalImages + totalVideos;

                  if (currentImageIndex < totalImages) {
                    // 画像を表示
                    return (
                      <img
                        src={`data:image/jpeg;base64,${profile.images[currentImageIndex]}`}
                        alt={`${profile.business_name} - 画像 ${currentImageIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                    );
                  } else {
                    // 動画を表示
                    const videoIndex = currentImageIndex - totalImages;
                    return (
                      <div className="w-full h-full bg-black flex items-center justify-center relative">
                        <video
                          controls
                          className="w-full h-full object-contain"
                          poster={profile.thumbnail_image ? `data:image/jpeg;base64,${profile.thumbnail_image}` : undefined}
                        >
                          <source src={`data:video/mp4;base64,${profile.videos[videoIndex]}`} type="video/mp4" />
                          お使いのブラウザは動画再生をサポートしていません。
                        </video>
                        <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                          動画
                        </div>
                      </div>
                    );
                  }
                })()}

                {/* ナビゲーション矢印 - タッチ対応改善 */}
                {((profile.images && profile.images.length > 0) || (profile.videos && profile.videos.length > 0)) &&
                  ((profile.images ? profile.images.length : 0) + (profile.videos ? profile.videos.length : 0)) > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-70 transition-opacity z-10 text-sm sm:text-base"
                      >

                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-full hover:bg-opacity-70 transition-opacity z-10 text-sm sm:text-base"
                      >

                      </button>
                    </>
                  )}

                {/* スライドインジケーター - モバイル対応 */}
                {((profile.images && profile.images.length > 0) || (profile.videos && profile.videos.length > 0)) &&
                  ((profile.images ? profile.images.length : 0) + (profile.videos ? profile.videos.length : 0)) > 1 && (
                    <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1 sm:space-x-2 z-10">
                      {[...Array((profile.images ? profile.images.length : 0) + (profile.videos ? profile.videos.length : 0))].map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all border-2 ${index === currentImageIndex
                            ? 'bg-white border-white'
                            : 'bg-transparent border-white border-opacity-50'
                            }`}
                        />
                      ))}
                    </div>
                  )}

                {/* メディア番号表示 - モバイル対応 */}
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs sm:text-sm z-10">
                  {currentImageIndex + 1} / {(profile.images ? profile.images.length : 0) + (profile.videos ? profile.videos.length : 0)}
                </div>
              </>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                <UserGroupIcon className="h-16 w-16 sm:h-20 sm:w-20 text-gray-500" />
              </div>
            )}

            {/* カテゴリバッジ - モバイル対応 */}
            <div className="absolute top-2 sm:top-4 left-2 sm:left-4 z-10">
              {profile.categories && profile.categories.length > 0 ? (
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  {profile.categories.slice(0, 2).map((category, index) => (
                    <span key={index} className="bg-blue-500 text-white text-xs sm:text-sm px-2 py-1 rounded-full">
                      {category}
                    </span>
                  ))}
                  {profile.categories.length > 2 && (
                    <span className="bg-gray-500 text-white text-xs sm:text-sm px-2 py-1 rounded-full">
                      +{profile.categories.length - 2}
                    </span>
                  )}
                </div>
              ) : (
                <span className="bg-gray-500 text-white text-xs sm:text-sm px-2 py-1 rounded-full">
                  未分類
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 基本情報 */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex justify-between items-start mb-4 sm:mb-6">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{profile.business_name}</h1>
              <div className="flex items-center text-gray-600 mb-2 sm:mb-4">
                <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
                <span className="text-sm sm:text-base">{profile.prefecture} {profile.city}</span>
              </div>
            </div>
          </div>

          {/* 勤務条件カード - モバイル対応 */}
          {(profile.work_hours || profile.hourly_rate) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-green-800 mb-2 sm:mb-3">勤務条件</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                {profile.work_hours && (
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium text-green-700">{profile.work_hours}</span>
                  </div>
                )}
                {profile.hourly_rate && (
                  <div className="flex items-center">
                    <CurrencyYenIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 flex-shrink-0" />
                    <span className="text-sm sm:text-base font-medium text-green-700">時給 {profile.hourly_rate.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">事業紹介</h2>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
              {profile.description}
            </p>
          </div>

          {/* カテゴリ表示 */}
          {profile.categories && profile.categories.length > 0 && (
            <div className="mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                <TagIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                事業カテゴリ
              </h2>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {profile.categories.map((category, index) => (
                  <span key={index} className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium">
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* タグ */}
          {profile.tags && profile.tags.length > 0 && (
            <div className="mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">特徴・条件</h2>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {profile.tags.map((tag, index) => (
                  <span key={index} className="bg-gray-100 text-gray-700 px-2 sm:px-3 py-1 sm:py-2 rounded-lg text-xs sm:text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 求職者へのメッセージ */}
          {profile.message_to_jobseekers && (
            <div className="mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">求職者へのメッセージ</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {profile.message_to_jobseekers}
                </p>
              </div>
            </div>
          )}

          {/* 店舗の評価セクション */}
          <EmployerRatingSection profileId={profileId} />

          {/* 所在地詳細とマップ */}
          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2" />
              所在地・アクセス
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* 住所情報 */}
              <div className="space-y-3">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">住所</h3>
                  <p className="text-gray-600">
                    {profile.prefecture} {profile.city}
                    {profile.address && (
                      <>
                        <br />
                        {profile.address}
                      </>
                    )}
                  </p>
                </div>

                {profile.work_location && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">アクセス</h3>
                    <p className="text-gray-600">{profile.work_location}</p>
                  </div>
                )}

                {/* 連絡先情報 */}
                {(profile.phone || profile.email) && (
                  <div className="pt-4 border-t border-gray-100">
                    <h3 className="font-medium text-gray-900 mb-2">連絡先</h3>
                    <div className="space-y-2">
                      {profile.phone && (
                        <div className="flex items-center text-gray-600">
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          <span>{profile.phone}</span>
                        </div>
                      )}
                      {profile.email && (
                        <div className="flex items-center text-gray-600">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          <span>{profile.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* マップ表示 */}
              <div className="md:col-span-2">
                <h3 className="font-medium text-gray-900 mb-3">地図・周辺事業者</h3>
                <EmployerLocationMap
                  employer={profile}
                  onEmployerClick={(employerId) => navigate(`/employer-profile/${employerId}`)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 求人情報詳細 */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            募集要項
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* 勤務条件 */}
            <div className="space-y-3 sm:space-y-4">
              {profile.work_hours && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">勤務時間</h3>
                  <p className="text-gray-600 flex items-center text-sm sm:text-base">
                    <ClockIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    {profile.work_hours}
                  </p>
                </div>
              )}

              {profile.hourly_rate && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">時給</h3>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">
                    {profile.hourly_rate.toLocaleString()}
                  </p>
                </div>
              )}

              {profile.max_people && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">募集人数</h3>
                  <p className="text-gray-600 flex items-center text-sm sm:text-base">
                    <UserGroupIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    {profile.max_people}名
                  </p>
                </div>
              )}
            </div>

            {/* 応募条件・特徴 */}
            <div className="space-y-3 sm:space-y-4">
              {profile.experience_required !== undefined && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">経験</h3>
                  <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm ${profile.experience_required
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-green-100 text-green-800'
                    }`}>
                    {profile.experience_required ? '経験者優遇' : '未経験歓迎'}
                  </span>
                </div>
              )}

              {profile.same_day_ok !== undefined && profile.same_day_ok && (
                <div>
                  <span className="inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm bg-blue-100 text-blue-800">
                    当日応募OK
                  </span>
                </div>
              )}

              {profile.daily_payment !== undefined && profile.daily_payment && (
                <div>
                  <span className="inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm bg-purple-100 text-purple-800">
                    日払い対応
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* 募集期間 */}
          {(profile.publication_start_date || profile.publication_end_date) && (
            <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
              <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">募集期間</h3>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <div className="flex items-center text-green-700">
                  <CalendarDaysIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base">
                    {profile.publication_start_date && new Date(profile.publication_start_date).toLocaleDateString('ja-JP')}
                    {(profile.publication_start_date && profile.publication_end_date) && ' 〜 '}
                    {profile.publication_end_date && new Date(profile.publication_end_date).toLocaleDateString('ja-JP')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 申請フォーム (求職者のみ) */}
        {user && user.user_type === 'job_seeker' ? (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">マッチング申請</h2>

            {identityVerified ? (
              <>
                {/* 募集期間 */}
                {(profile.publication_start_date || profile.publication_end_date) && (
                  <div className="mb-3 sm:mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      募集期間
                    </label>
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <div className="flex items-center text-green-700">
                        <CalendarDaysIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="font-medium text-sm sm:text-base">
                          {profile.publication_start_date && new Date(profile.publication_start_date).toLocaleDateString('ja-JP')}
                          {(profile.publication_start_date && profile.publication_end_date) && ' 〜 '}
                          {profile.publication_end_date && new Date(profile.publication_end_date).toLocaleDateString('ja-JP')}
                        </span>
                      </div>
                      {profile.publication_end_date && (
                        <p className="text-xs text-green-600 mt-1">
                          この期間に勤務可能な方を募集しています
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* 募集中の勤務時間帯 */}
                {profile.work_hours && (
                  <div className="mb-3 sm:mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      募集中の勤務時間帯
                    </label>
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center text-blue-700">
                        <ClockIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="font-medium text-sm sm:text-base">{profile.work_hours}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* 応募資格の不備表示 */}
                {!isEligible && eligibilityIssues.length > 0 && (
                  <div className="mb-4 sm:mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          応募に必要な項目に不備があります
                        </h3>
                        <div className="mt-2 text-sm text-red-700">
                          <ul className="list-disc list-inside space-y-1">
                            {eligibilityIssues.map((issue, index) => (
                              <li key={index}>{issue}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="mt-3">
                          <button
                            onClick={() => navigate('/mypage?tab=profile')}
                            className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200 transition-colors"
                          >
                            プロフィールを完成させる
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 希望勤務時間設定 */}
                <div className="mb-3 sm:mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    あなたの希望勤務時間 <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">開始時間</label>
                      <input
                        type="time"
                        value={preferredWorkStartTime}
                        onChange={(e) => setPreferredWorkStartTime(e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">終了時間</label>
                      <input
                        type="time"
                        value={preferredWorkEndTime}
                        onChange={(e) => setPreferredWorkEndTime(e.target.value)}
                        className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                        required
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    募集時間内で、あなたが働ける時間帯を設定してください
                  </p>
                </div>

                {/* 希望日選択 */}
                <div className="mb-3 sm:mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    希望勤務日（任意）
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* メッセージ */}
                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    メッセージ（任意）
                  </label>
                  <textarea
                    value={applicationMessage}
                    onChange={(e) => setApplicationMessage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                    rows={4}
                    placeholder="自己アピールや意気込みをお書きください"
                  />
                </div>

                <button
                  onClick={handleApply}
                  disabled={applying || !isEligible}
                  className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center text-sm sm:text-base ${!isEligible
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                >
                  {applying ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      申請中...
                    </>
                  ) : !isEligible ? (
                    '応募要件未達成のため申請できません'
                  ) : (
                    'マッチング申請を送信'
                  )}
                </button>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="mb-4 text-gray-600">本人確認完了後に応募できるようになります</p>
                <button onClick={() => navigate('/identity-verification')} className="text-blue-600 underline">本人確認を行う</button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6 text-center">
            <h2 className="text-lg font-bold text-gray-900 mb-2">この求人に応募するには？</h2>
            <p className="text-gray-600 mb-4">応募するには、求職者としてのログインが必要です。</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:bg-blue-700 transition"
            >
              ログインして応募する
            </button>
            <p className="mt-4 text-sm text-gray-500">
              まだアカウントをお持ちでない方は <button onClick={() => navigate('/register')} className="text-blue-600 underline">こちら</button>
            </p>
          </div>
        )}

        {
          user && user.user_type !== 'job_seeker' && (
            <div className="bg-gray-100 rounded-lg p-6 text-center">
              <p className="text-gray-600">
                {user.user_type === 'employer'
                  ? '事業者として申請することはできません'
                  : 'ログインして申請してください'}
              </p>
            </div>
          )
        }

        {
          !user && (
            <div className="bg-blue-50 rounded-lg p-6 text-center">
              <p className="text-blue-800 mb-4">マッチング申請をするにはログインが必要です</p>
              <button
                onClick={() => navigate('/login')}
                className="bg-blue-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
              >
                ログインして申請
              </button>
            </div>
          )
        }
      </div >

      {/* 動画モーダル */}
      {
        showVideoModal && profile.videos && profile.videos.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-4 max-w-4xl w-full mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">紹介動画</h3>
                <button
                  onClick={() => setShowVideoModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >

                </button>
              </div>
              <video
                src={`data:video/mp4;base64,${profile.videos[0]}`}
                controls
                className="w-full rounded-lg"
                autoPlay
              />
            </div>
          </div>
        )
      }
    </div >
  );
};

export default EmployerProfileDetail;